import React, { useState, useEffect, useCallback, useRef } from 'react'
import './index.scss'
import { Modal } from 'antd'
import sensors, { sensorsRole } from '../../../utils/sensors'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'

import Footer from './components/Footer'
import Content from './components/content'
import NamingSection from './components/NamingSection'
import { ModalHeader, ModalCloseIcon } from './components/ModalHeader'
import {
  calcCheckedPhotoNum,
  sensorsClassification
} from '@/pages/components/photoLib/photo'

import { useFolderState } from './hooks/useFolderState'
import { useNamingLogic } from './hooks/useNamingLogic'
import { useTrial } from './hooks/useTrial'
import CONFIG from '../../../constants/config'
import {
  saveSelection,
  loadSelection,
  loadNamingRule,
  loadFolderTitles
} from './utils/storage'
import {
  transformNamingRuleForServer,
  transformFolderLevelsForServer,
  sensorsFolderType,
  sensorsNameRule,
  generateFolderNamingParams
} from './utils'
import { fetchExportList } from '../../../actions/photo'
import { buildFetchPhotosParamsWithOptions } from '../../ai-photo-king/lib/photo'

interface FolderModalProps {
  visible: boolean
  photoTitleList?: any[]
  folderTitleList?: any[]
  onOk?: (...args: any[]) => void
  onDownloadOnce?: (...args: any[]) => void
  onClose?: () => void
  disableBatchDownload?: boolean
  include?: string[]
  exclude?: string[]
}

const FolderModal: React.FC<FolderModalProps> = ({
  visible,
  photoTitleList = [],
  folderTitleList = [],
  onOk,
  onDownloadOnce,
  onClose,
  disableBatchDownload,
  include: includeProp,
  exclude: excludeProp
}) => {
  const dispatch = useAppDispatch()
  const myRole = useAppSelector((state) => state.group.myRole)
  const isVip = useAppSelector((state) => state.group.isVip)
  const vipKind = useAppSelector((state) => state.group.vipInfo?.vip_kind)
  const photoState = useAppSelector((state) => state.photo)
  const groupId = useAppSelector((state) => state.group.groupId)

  // 获取搜索筛选条件
  const photov2Filter = useAppSelector(
    (state) => state.photoFilter?.searchFilter || state.photoFilter?.uiFilter
  )
  const searchFilter = photov2Filter
  // 状态管理
  const [folderTitles, setFolderTitles] = useState('watermark')

  // 自定义 hooks
  const { folderLevels, handleLevelChange } = useFolderState()
  const { namingRule, onChangeSelectNameData } = useNamingLogic()
  const {
    multiLeft: multiTrialLeft,
    renameLeft: renameTrialLeft,
    renameLimit: renameTrialLimit
  } = useTrial({
    autoFetch: true,
    visible
  })
  const [sampleFiles, setSampleFiles] = useState([])
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 获取照片数据构建文件夹预览
  const fetchExportSamples = async () => {
    if (!visible) return
    try {
      const folderV2 = transformFolderLevelsForServer(folderLevels)
      const baseFilter =
        typeof buildFetchPhotosParamsWithOptions === 'function'
          ? buildFetchPhotosParamsWithOptions({
              queryFilter: (searchFilter || {}) as any,
              groupID: groupId,
              lastPageCond: ''
            })
          : { groupID: groupId }

      // 文件夹规则和文件夹标题
      let folderRuleLocal =
        folderLevels && folderLevels[0] ? folderLevels[0].field : ''
      let folderTitlesLocal = folderTitles ? [folderTitles] : []
      if (folderRuleLocal !== 'customize') {
        folderTitlesLocal = []
      } else if (
        Array.isArray(folderTitlesLocal) &&
        folderTitlesLocal.join('') === 'watermark'
      ) {
        folderRuleLocal = 'watermark'
        folderTitlesLocal = []
      }

      const requestBody = {
        ...baseFilter,
        pageSize: 100,
        scene: 1,
        namePolicy: {
          file: namingRule || [],
          folder: folderRuleLocal,
          folderTitles: folderTitlesLocal,
          folderV2: folderV2
        }
      }
      // 包含/排除
      if (!requestBody.filter) requestBody.filter = {}
      // 包含/排除
      requestBody.filter.include = Array.isArray(includeProp) ? includeProp : []
      requestBody.filter.exclude = Array.isArray(excludeProp) ? excludeProp : []

      const result = await dispatch(fetchExportList(requestBody))
      const files = result?.files || result?.payload?.files || []
      setSampleFiles(files)
    } catch (err) {
      console.error(err)
      setSampleFiles([])
    }
  }

  // 初始化数据
  useEffect(() => {
    if (visible) {
      // 加载命名规则
      const savedNamingRule = loadNamingRule(photoTitleList)
      if (savedNamingRule) {
        onChangeSelectNameData(savedNamingRule)
      }

      // 加载文件夹配置
      const savedFolderLevels = loadSelection()
      if (savedFolderLevels) {
        for (let i = 0; i < savedFolderLevels.length; i++) {
          handleLevelChange(i + 1, savedFolderLevels[i]?.field || '')
        }
      }

      // 加载文件夹标题
      const savedFolderTitles = loadFolderTitles(folderTitleList)
      setFolderTitles(savedFolderTitles || 'watermark')
    }
  }, [
    visible,
    onChangeSelectNameData,
    handleLevelChange,
    folderTitleList,
    photoTitleList
  ])
  // 获取照片数据构建文件夹预览
  useEffect(() => {
    if (!visible) return
    // 防抖
    const controller = new AbortController()
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    fetchTimerRef.current = setTimeout(() => {
      fetchExportSamples()
    }, 300)
    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
      controller.abort()
    }
    // 只在可见时重新获取
  }, [visible, folderLevels, folderTitles, groupId])
  // 生成每一级文件夹的命名规则参数
  const generateFolderNamingParamsForModal = () => {
    return generateFolderNamingParams(
      folderLevels,
      photoTitleList,
      multiTrialLeft,
      renameTrialLeft
    )
  }

  // 埋点方法
  const track = (source: string, params: Record<string, any>) => {
    if (!source) return
    sensors.track('web_click_page_workgroup_pic_download', {
      clickItem: source || 'download',
      role: sensorsRole(myRole),
      groupID: groupId,
      isVip,
      photoNum: calcCheckedPhotoNum(photoState),
      classification: sensorsClassification(folderLevels[0]?.field || ''),
      ...params
    })
  }

  // 下载单张图片
  const handleDownloadOnce = useCallback(() => {
    // 缓存选择
    saveSelection(folderLevels, folderTitles, namingRule)

    // 数据转换
    const transformedNamingRule = transformNamingRuleForServer(namingRule)
    const folderV2 = transformFolderLevelsForServer(folderLevels)

    // 调用父组件方法
    onDownloadOnce?.(
      folderLevels[0]?.field || '',
      transformedNamingRule,
      [folderTitles],
      folderV2
    )
  }, [folderLevels, folderTitles, namingRule, onDownloadOnce])

  // 处理确认下载；namingRuleOverride 用于「去掉高级命名」等场景，避免闭包仍是旧 namingRule
  const handleOk = useCallback(
    (source, namingRuleOverride?) => {
      const ruleToUse =
        namingRuleOverride !== undefined && namingRuleOverride !== null
          ? namingRuleOverride
          : namingRule

      // 缓存选择
      saveSelection(folderLevels, folderTitles, ruleToUse)

      // 埋点跟踪
      sensors.track('web_workgroup_export_setpage_click', {
        folderType: sensorsFolderType(folderLevels[0]?.field || ''),
        nameRule: sensorsNameRule(ruleToUse, photoTitleList)
      })
      track(source, {})

      // 数据转换
      const transformedNamingRule = transformNamingRuleForServer(ruleToUse)
      const folderV2 = transformFolderLevelsForServer(folderLevels)

      // 调用父组件方法
      onOk?.(
        folderLevels[0]?.field || '',
        transformedNamingRule,
        [folderTitles],
        source || 'download',
        generateFolderNamingParamsForModal(),
        folderV2
      )
    },
    [
      folderLevels,
      folderTitles,
      namingRule,
      myRole,
      groupId,
      isVip,
      photoState,
      onOk,
      photoTitleList,
      multiTrialLeft,
      renameTrialLeft
    ]
  )

  // 处理文件夹标题变化
  const handleFolderTitlesChange = useCallback((newFolderTitles) => {
    setFolderTitles(newFolderTitles)
  }, [])

  // 渲染组件
  return (
    <Modal
      open={visible}
      className="folder-modal folder-style"
      getContainer={false}
      width={752}
      centered
      title={<ModalHeader title="图片/视频下载" isDisplayHint={!isVip} />}
      mask={{ closable: false }}
      closeIcon={
        <ModalCloseIcon
          onClose={onClose}
          myRole={myRole}
          groupId={groupId}
          isVip={isVip}
          photoState={photoState}
        />
      }
      footer={
        <Footer
          namingRule={namingRule}
          onCancel={onClose}
          onOk={handleOk}
          onDownloadOnce={handleDownloadOnce}
          disableBatchDownload={disableBatchDownload}
          folderLevels={folderLevels}
          photoTitleList={photoTitleList}
          track={track}
          myRole={myRole}
          onChangeSelectNameData={onChangeSelectNameData}
        />
      }
    >
      <Content
        folderLevels={folderLevels}
        photoTitleList={photoTitleList}
        sampleFiles={sampleFiles}
        onChangeLevel={handleLevelChange}
        isVip={isVip}
        multiTrialLeft={multiTrialLeft}
        track={track}
        visible={visible}
      />
      <NamingSection
        namingRule={namingRule}
        photoTitleList={photoTitleList}
        onChangeSelectNameData={onChangeSelectNameData}
        vipKind={vipKind}
        isVip={isVip}
        renameTrialLeft={renameTrialLeft}
        renameTrialLimit={renameTrialLimit}
        track={track}
      />
    </Modal>
  )
}

export default FolderModal
